import { useMemo } from "react";
import { Flag, MapPin, TrendingDown, TrendingUp } from "lucide-react";
import { clockTime } from "@/lib/format";
import { speedBandColor } from "./MapCanvas";
import { cn } from "@/lib/utils";

/**
 * The trip as one horizontal run: green dot at the start, red at the end, and
 * the line between them standing for the whole trip.
 *
 * Two things are drawn on that line, and they are drawn differently on purpose:
 *
 *  - **The speed profile.** Real telemetry from trip_locations, positioned by
 *    elapsed time and coloured with the same speed bands the map uses for the
 *    route. Sampling is roughly every 5 seconds, which is dense enough to show
 *    where the vehicle slowed, stopped and accelerated.
 *
 *  - **Events.** Only things with a real timestamp get a dot, placed at their
 *    true fraction of the trip.
 *
 * Hard braking and rapid acceleration are shown as counts beside the header,
 * not as dots. Bouncie's tripMetrics payload reports a total for the whole trip
 * with no timestamp or coordinate for the individual events, so there is no
 * position to draw them at. Deriving positions from the GPS track was
 * considered: at 5-second sampling a one-to-three-second brake is averaged
 * away, so the derived count would contradict the count shown in the stats
 * directly below. A dot in the wrong place is worse than no dot.
 */

// The dots sit inset from the card edges so their outer ring is not clipped;
// every position along the track is measured inside that inset.
const INSET = "0.875rem";

// Bright enough to read against the dark card, and distinct from the speed
// bands so an event is never mistaken for a change of pace.
const SEVERITY_COLOR = {
  critical: "#f43f5e",
  warning: "#fbbf24",
  info: "#38bdf8",
};

function eventColor(severity) {
  return SEVERITY_COLOR[severity] || SEVERITY_COLOR.info;
}

function trackLeft(position) {
  return `calc(${INSET} + ${Math.min(Math.max(position, 0), 1)} * (100% - 2 * ${INSET}))`;
}

/**
 * Speed profile as an SVG polyline, one segment per sample so each can take
 * its own band colour.
 *
 * viewBox is a fixed 1000x100 with preserveAspectRatio="none" so the shape
 * stretches to whatever width the card has without recomputing on resize.
 */
function SpeedProfile({ points, startTime, spanSeconds }) {
  const segments = useMemo(() => {
    if (!points?.length || !startTime || !spanSeconds) return [];

    const usable = points.filter((p) => p.recordedAt && p.speed != null);
    if (usable.length < 2) return [];

    const peak = Math.max(...usable.map((p) => p.speed), 1);
    const x = (p) => {
      const elapsed = (p.recordedAt.getTime() - startTime.getTime()) / 1000;
      return Math.min(Math.max(elapsed / spanSeconds, 0), 1) * 1000;
    };
    // 6 leaves a visible floor for a stationary sample, so a stopped vehicle
    // reads as "stopped here" rather than as missing data.
    const y = (p) => 94 - (p.speed / peak) * 88;

    const out = [];
    for (let i = 1; i < usable.length; i += 1) {
      const a = usable[i - 1];
      const b = usable[i];
      out.push({
        x1: x(a), y1: y(a), x2: x(b), y2: y(b),
        // Coloured by the faster of the pair: a segment that crosses into a
        // band is more usefully flagged as reaching it than as leaving the one
        // below.
        color: speedBandColor(Math.max(a.speed, b.speed)),
      });
    }
    return out;
  }, [points, startTime, spanSeconds]);

  if (!segments.length) return null;

  return (
    <svg
      viewBox="0 0 1000 100"
      preserveAspectRatio="none"
      className="h-14 w-full"
      aria-hidden="true"
    >
      {segments.map((s, i) => (
        <line
          key={i}
          x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
          stroke={s.color}
          strokeWidth="2.5"
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

export function TripTimeline({ trip, route, events = [], counts, className }) {
  const spanSeconds = useMemo(() => {
    if (!trip?.startTime) return null;
    const end = trip.endTime || new Date();
    return Math.max((end.getTime() - trip.startTime.getTime()) / 1000, 0) || null;
  }, [trip]);

  const inProgress = trip?.status === "in-progress";
  const hasProfile = Boolean(route?.length && spanSeconds);

  return (
    <div className={cn("rounded-2xl border border-border/60 bg-card/60 p-4", className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
        <h3 className="font-display text-sm font-medium">Trip timeline</h3>
        {/* Counts, not dots — see the note at the top of this file. */}
        <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <TrendingDown className="h-3.5 w-3.5 text-amber-400" />
            {counts?.hardBraking ?? trip?.hardBraking ?? 0} hard braking
          </span>
          <span className="inline-flex items-center gap-1.5">
            <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
            {counts?.hardAccel ?? trip?.hardAccel ?? 0} hard acceleration
          </span>
        </div>
      </div>

      {hasProfile ? (
        <div className="mt-3" style={{ paddingLeft: INSET, paddingRight: INSET }}>
          <SpeedProfile points={route} startTime={trip.startTime} spanSeconds={spanSeconds} />
        </div>
      ) : (
        <div className="mt-3 h-14" aria-hidden="true" />
      )}

      {/* The track. Everything on it is absolutely positioned against the same
          inset, so a dot at position 0.5 sits exactly halfway between the two
          end markers rather than halfway across the card. */}
      <div className="relative h-7">
        <div
          className="absolute top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-border"
          style={{ left: INSET, right: INSET }}
          aria-hidden="true"
        />

        <Marker className="left-0" tone="border-emerald-400/40 bg-emerald-400/15 text-emerald-400">
          <Flag className="h-3 w-3" />
        </Marker>
        <Marker className="right-0" tone="border-red-500/40 bg-red-500/15 text-red-400">
          <MapPin className="h-3 w-3" />
        </Marker>

        {events.map((e) => (
          <button
            key={e.id}
            type="button"
            // A real title attribute rather than a custom tooltip: this needs
            // to work on a printed page and under a finger, and both fall back
            // to the accessible name.
            title={`${e.title} · ${clockTime(e.occurredAt, trip?.utcOffsetMinutes)}`}
            className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-card transition hover:scale-125 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            style={{ left: trackLeft(e.position), background: eventColor(e.severity) }}
          >
            <span className="sr-only">{e.title}</span>
          </button>
        ))}
      </div>

      <div className="mt-2 flex items-start justify-between gap-4">
        <End
          time={clockTime(trip?.startTime, trip?.utcOffsetMinutes)}
          place={trip?.startLoc}
          label="Started"
        />
        <End
          align="right"
          time={trip?.endTime ? clockTime(trip.endTime, trip.utcOffsetMinutes) : "—"}
          place={trip?.endLoc}
          label={inProgress ? "Still driving" : "Ended"}
        />
      </div>

      {events.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border/50 pt-2.5 text-xs text-muted-foreground">
          {events.map((e) => (
            <span key={e.id} className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: eventColor(e.severity) }} />
              {e.title}
              <span className="font-telemetry">{clockTime(e.occurredAt, trip?.utcOffsetMinutes)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Marker({ className, tone, children }) {
  return (
    <div
      className={cn(
        "absolute top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full border",
        tone,
        className
      )}
    >
      {children}
    </div>
  );
}

function End({ time, place, label, align = "left" }) {
  return (
    <div className={cn("min-w-0 max-w-[45%]", align === "right" && "text-right")}>
      <div className="flex items-baseline gap-1.5" style={align === "right" ? { justifyContent: "flex-end" } : undefined}>
        <span className="text-xs font-medium">{label}</span>
        <span className="font-telemetry text-xs text-muted-foreground">{time}</span>
      </div>
      <div className="truncate text-xs text-muted-foreground" title={place}>{place}</div>
    </div>
  );
}
